import * as THREE from 'three';

/**
 * Doors, lifts and buttons — the simulation, with no scene graph in it.
 *
 * ## Why this is its own file
 *
 * The movers were built and ticked inside `CrossfireLevel`, which needs a
 * renderer, a texture factory and a WebGL context. The game server has none of
 * those, so it has never had movers at all: online, every lift and door on
 * Crossfire was non-collidable. The client predicted you standing on a
 * platform, the server said you were in mid-air, and reconciliation dragged you
 * back down — "elevators don't work".
 *
 * So the state and the tick live here, in plain data, and both sides run the
 * SAME code over the SAME definitions. `CrossfireLevel` still owns the meshes
 * and just follows `mover.pos` each frame; the server owns nothing else.
 *
 * ## Half-Life's func_door semantics, which this reimplements
 *
 *  - a door slides between a home and an away position along a fixed offset;
 *  - `speed` is metres per second along that offset;
 *  - `wait` is how long it holds at the far end before returning, and -1 means
 *    it never returns on its own;
 *  - spawnflags bit 0 means the door was authored OPEN, so its rest state is
 *    the far end and triggering closes it.
 */
export class MoverSet {
  /**
   * @param {Array} defs plain mover definitions — see `serialise()` for the
   *   shape. Boxes arrive as [min[3], max[3]] arrays so the whole thing
   *   survives JSON.
   * @param {Array} buttonDefs
   */
  constructor(defs = [], buttonDefs = []) {
    this.movers = defs.map((d) => ({
      name: d.name ?? '',
      /** Where the mover sits right now, relative to its authored position. */
      pos: new THREE.Vector3(),
      home: new THREE.Vector3().fromArray(d.home),
      away: new THREE.Vector3().fromArray(d.away),
      t: d.startsOpen ? 1 : 0,
      dir: 0,
      speed: d.speed,
      length: d.length,
      wait: d.wait,
      waitTimer: 0,
      startsOpen: !!d.startsOpen,
      delta: new THREE.Vector3(),
      solid: true,
      baseBox: box3(d.box),
      box: box3(d.box),
      /**
       * What collision resolves against: one box per part, not their union.
       * A lift is a piston and a platform, and the union of those is a solid
       * block four metres tall of which 85% is air.
       */
      baseParts: (d.parts ?? [d.box]).map(box3),
      parts: (d.parts ?? [d.box]).map(box3),
    }));

    this.buttons = buttonDefs.map((b) => ({
      target: b.target,
      centre: new THREE.Vector3().fromArray(b.centre),
      cooldown: 0,
    }));

    /** targetname -> movers, so one button can drive a pair of doors. */
    this.byName = new Map();
    for (const m of this.movers) {
      const key = m.name || `__anon${this.byName.size}`;
      if (!this.byName.has(key)) this.byName.set(key, []);
      this.byName.get(key).push(m);
    }

    for (const m of this.movers) this.place(m);
  }

  /** Recompute a mover's boxes from `t`, and record how far it just moved. */
  place(m) {
    _prev.copy(m.pos);
    m.pos.lerpVectors(m.home, m.away, m.t);
    m.delta.subVectors(m.pos, _prev);
    m.box.copy(m.baseBox).translate(m.pos);
    for (let i = 0; i < m.parts.length; i++) {
      m.parts[i].copy(m.baseParts[i]).translate(m.pos);
    }
  }

  update(dt) {
    for (const b of this.buttons) if (b.cooldown > 0) b.cooldown -= dt;

    for (const m of this.movers) {
      if (m.dir === 0) {
        m.delta.set(0, 0, 0);
        // Auto-return after `wait`, unless the map says never (-1).
        if (m.wait >= 0 && m.t >= 1 && !m.startsOpen) {
          m.waitTimer += dt;
          if (m.waitTimer >= m.wait) { m.dir = -1; m.waitTimer = 0; }
        }
        continue;
      }
      const step = (m.speed / Math.max(0.001, m.length)) * dt * m.dir;
      const next = clamp(m.t + step, 0, 1);
      const done = next === m.t || next === 0 || next === 1;
      m.t = next;
      this.place(m);
      if (done && (next === 0 || next === 1)) m.dir = 0;
    }
  }

  /** Fire whatever a button targets. Returns true if anything moved. */
  trigger(targetName) {
    const list = this.byName.get(targetName);
    if (!list?.length) return false;
    for (const m of list) {
      // Toggle: head for whichever end we are not already at.
      m.dir = m.t > 0.5 ? -1 : 1;
      m.waitTimer = 0;
    }
    return true;
  }

  /** Nearest usable button within range, or null. */
  nearestButton(position, maxDist = 2.6) {
    let best = null, bestD = maxDist * maxDist;
    for (const b of this.buttons) {
      const d = b.centre.distanceToSquared(position);
      if (d < bestD) { bestD = d; best = b; }
    }
    return best;
  }

  /** Press the nearest button. Returns its target name, or null. */
  use(position, maxDist = 2.6) {
    const best = this.nearestButton(position, maxDist);
    if (!best || best.cooldown > 0) return null;
    best.cooldown = 1.0;
    return this.trigger(best.target) ? best.target : null;
  }
}

/**
 * The definitions, as data.
 *
 * Written by tools/export-collision.mjs into the server's map payload, so the
 * server gets movers without ever constructing a mesh.
 */
export function serialiseMovers(movers, buttons) {
  return {
    movers: movers.map((m) => ({
      name: m.name,
      home: m.home.toArray(),
      away: m.away.toArray(),
      speed: m.speed,
      length: m.length,
      wait: m.wait,
      startsOpen: !!m.startsOpen,
      box: [m.baseBox.min.toArray(), m.baseBox.max.toArray()],
      parts: (m.baseParts ?? []).map((b) => [b.min.toArray(), b.max.toArray()]),
    })),
    buttons: buttons.map((b) => ({ target: b.target, centre: b.centre.toArray() })),
  };
}

function box3(pair) {
  return new THREE.Box3(
    new THREE.Vector3().fromArray(pair[0]),
    new THREE.Vector3().fromArray(pair[1])
  );
}

const clamp = (v, a, b) => (v < a ? a : v > b ? b : v);
const _prev = new THREE.Vector3();
